import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  LinearProgress,
  Chip,
  CircularProgress,
  Link,
} from '@mui/material';
import { adminApi } from '../services/api';
import { parseUsersCsv, ParsedUserRow as ParsedRow } from './parseUsersCsv';

interface BulkJobStatus {
  status: 'QUEUED' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  progress: number;
  totalUsers: number;
  processed: number;
  succeeded: number;
  failed: number;
  results: Array<{ email: string; status: 'created' | 'exists' | 'failed'; userId?: string; errorMessage?: string }>;
  errorMessage?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  organizations: { id: string; name: string }[];
  onCompleted: () => void;
}

const TEMPLATE_CSV = 'email,firstName,lastName\njane.doe@example.com,Jane,Doe\njohn.smith@example.com,John,Smith\n';

const BulkAddUsersDialog: React.FC<Props> = ({ open, onClose, organizations, onCompleted }) => {
  const [organizationId, setOrganizationId] = useState('');
  const [sendInvitation, setSendInvitation] = useState(true);
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<BulkJobStatus | null>(null);
  const [pollError, setPollError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setOrganizationId('');
    setSendInvitation(true);
    setRows([]);
    setParseError(null);
    setSubmitting(false);
    setJobId(null);
    setJobStatus(null);
    setPollError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    if (jobStatus?.status === 'COMPLETED' || jobStatus?.status === 'FAILED') {
      onCompleted();
    }
    reset();
    onClose();
  };

  const handleFile = (file: File) => {
    setParseError(null);
    file.text().then((text) => {
      const { rows: parsed, error } = parseUsersCsv(text);
      if (error) {
        setParseError(error);
        return;
      }
      setRows(parsed);
    }).catch((err) => setParseError(`Failed to read file: ${err.message}`));
  };

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE_CSV], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bulk-users-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const validRows = rows.filter((r) => !r.error);

  const pollStatus = async (id: string) => {
    try {
      const response = await adminApi.getBulkCreateUsersJob(id);
      if (response.success) {
        const data = response.data as BulkJobStatus;
        setJobStatus(data);
        if (data.status === 'COMPLETED' || data.status === 'FAILED') {
          return;
        }
        setTimeout(() => pollStatus(id), 2000);
      }
    } catch (err: any) {
      setPollError(err.response?.data?.message || err.message || 'Failed to fetch job status');
    }
  };

  const handleSubmit = async () => {
    if (!organizationId || validRows.length === 0) return;
    try {
      setSubmitting(true);
      setPollError(null);
      const response = await adminApi.bulkCreateUsers({
        organizationId,
        sendInvitation,
        isPaid: true,
        users: validRows.map((r) => ({
          email: r.email,
          firstName: r.firstName,
          lastName: r.lastName,
        })),
      });
      if (response.success) {
        const id = response.data.jobId as string;
        setJobId(id);
        setJobStatus({
          status: 'QUEUED',
          progress: 0,
          totalUsers: validRows.length,
          processed: 0,
          succeeded: 0,
          failed: 0,
          results: [],
        });
        pollStatus(id);
      }
    } catch (err: any) {
      setPollError(err.response?.data?.message || err.message || 'Failed to start bulk job');
    } finally {
      setSubmitting(false);
    }
  };

  const downloadResultsCsv = () => {
    if (!jobStatus) return;
    const header = 'email,status,userId,error\n';
    const body = jobStatus.results
      .map((r) => `${r.email},${r.status},${r.userId || ''},"${(r.errorMessage || '').replace(/"/g, '""')}"`)
      .join('\n');
    const blob = new Blob([header + body], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bulk-users-results-${jobId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const inProgress = jobId && jobStatus && jobStatus.status !== 'COMPLETED' && jobStatus.status !== 'FAILED';
  const finished = jobStatus?.status === 'COMPLETED' || jobStatus?.status === 'FAILED';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Bulk Add Users</DialogTitle>
      <DialogContent>
        {!jobId && (
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth required>
              <InputLabel>Organization</InputLabel>
              <Select
                value={organizationId}
                label="Organization"
                onChange={(e) => setOrganizationId(e.target.value)}
              >
                {organizations.map((org) => (
                  <MenuItem key={org.id} value={org.id}>
                    {org.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Upload a CSV with columns: <code>email,firstName,lastName</code>.{' '}
                <Link component="button" type="button" onClick={downloadTemplate}>
                  Download template
                </Link>
              </Typography>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFile(file);
                }}
              />
            </Box>

            <FormControlLabel
              control={
                <Checkbox
                  checked={sendInvitation}
                  onChange={(e) => setSendInvitation(e.target.checked)}
                />
              }
              label="Send invitation emails"
            />

            {parseError && <Alert severity="error">{parseError}</Alert>}

            {rows.length > 0 && (
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {validRows.length} valid / {rows.length} total rows
                </Typography>
                <Box sx={{ maxHeight: 300, overflow: 'auto', border: 1, borderColor: 'divider' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Email</TableCell>
                        <TableCell>First</TableCell>
                        <TableCell>Last</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.slice(0, 100).map((r, i) => (
                        <TableRow key={i}>
                          <TableCell>{r.email}</TableCell>
                          <TableCell>{r.firstName || '-'}</TableCell>
                          <TableCell>{r.lastName || '-'}</TableCell>
                          <TableCell>
                            {r.error ? (
                              <Chip size="small" color="error" label={r.error} />
                            ) : (
                              <Chip size="small" color="success" label="OK" />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
                {rows.length > 100 && (
                  <Typography variant="caption">Showing first 100 of {rows.length}</Typography>
                )}
              </Box>
            )}
          </Box>
        )}

        {jobId && jobStatus && (
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body1">
              Job <code>{jobId}</code> — status: <strong>{jobStatus.status}</strong>
            </Typography>
            <LinearProgress variant="determinate" value={jobStatus.progress || 0} />
            <Typography variant="body2">
              Processed {jobStatus.processed} / {jobStatus.totalUsers} ·{' '}
              <span style={{ color: 'green' }}>{jobStatus.succeeded} succeeded</span> ·{' '}
              <span style={{ color: 'red' }}>{jobStatus.failed} failed</span>
            </Typography>

            {pollError && <Alert severity="error">{pollError}</Alert>}
            {jobStatus.errorMessage && <Alert severity="error">{jobStatus.errorMessage}</Alert>}

            {finished && jobStatus.results.length > 0 && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle2">Results</Typography>
                  <Button size="small" onClick={downloadResultsCsv}>
                    Download CSV
                  </Button>
                </Box>
                <Box sx={{ maxHeight: 300, overflow: 'auto', border: 1, borderColor: 'divider' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Email</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Error</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {jobStatus.results.map((r, i) => (
                        <TableRow key={i}>
                          <TableCell>{r.email}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              color={r.status === 'created' ? 'success' : 'error'}
                              label={r.status}
                            />
                          </TableCell>
                          <TableCell>{r.errorMessage || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{finished ? 'Close' : 'Cancel'}</Button>
        {!jobId && (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!organizationId || validRows.length === 0 || submitting}
          >
            {submitting ? <CircularProgress size={20} /> : `Create ${validRows.length} Users`}
          </Button>
        )}
        {!!inProgress && <CircularProgress size={20} />}
      </DialogActions>
    </Dialog>
  );
};

export default BulkAddUsersDialog;
